<template>
  <div>
    <div class="d-flex">
      <div class="mr-3">
        <v-button
          size="sm"
          text="word.back"
          icon="chevron-left"
          :route-to="{name: 'dashboard'}"
        />
      </div>
      <h3 v-if="research">
        {{ research.title }}
        <small class="text-muted">({{
            $tc('word.answer', 2)
              .toLowerCase()
          }})</small>
      </h3>
    </div>

    <div class="row" v-if="researchReviewSession">
      <div class="col-md-7">
        <div class="card bg-white">
          <div class="card-header">
            <h5 class="p-0 m-0">{{ $tc('word.answer', 2) }}</h5>
          </div>
          <div class="card-body bg-light p-2 pb-0 m-0">
            <div
              v-for="researchItem in researchItems"
              :key="researchItem.id"
            >
              <v-item-answer
                :research-item="researchItem"
                :organisation-name="research.organisation.name"
                :answer-value="researchItem.answer.value"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-5">
        <div class="card mb-3">
          <h6 class="card-header with-elements d-flex justify-content-between">
            <span class="card-header-title">
              <i class="fa fa-info text-cr-red mr-2"></i>
              {{ $t('phrase.research_info') }}
            </span>
          </h6>
          <table class="table mb-0" v-if="researchItems && research">
            <tr>
              <td style="width: 140px"><strong>{{ $tc('word.title', 1) }}</strong></td>
              <td>{{ research.title }}</td>
            </tr>
            <tr>
              <td><strong>{{ $tc('word.organisation', 1) }}</strong></td>
              <td>
                <v-organisation-router-link
                  :organisation-name="research.organisation.name"
                  :organisation-slug="research.organisation.slug"
                  :logo-url="research.organisation.logo_url"
                  :is-active="research.organisation.is_active"
                />
              </td>
            </tr>
            <tr v-if="research.created_by">
              <td><strong>{{ $t('phrase.developed_by') }}</strong></td>
              <td>
                <v-organisation-router-link
                  :organisation-name="research.created_by.name"
                  :organisation-slug="research.created_by.slug"
                  :logo-url="research.created_by.logo_url"
                  :is-active="research.created_by.is_active"
                />
              </td>
            </tr>
            <tr>
              <td><strong>{{ $t('phrase.questions_count') }}</strong></td>
              <td>{{ researchItems.length }}</td>
            </tr>
            <tr v-if="researchReviewSession">
              <td><strong>{{ $t('phrase.reviewed_at') }}</strong></td>
              <td>
                {{ stringToPrettyDateTime(researchReviewSession.reviewed) }}
              </td>
            </tr>
          </table>
        </div>
        <div
          v-if="organisations"
          class="card mb-3"
        >
          <router-link
            :to="{ name: 'public-organisation-list', query: { type: 'media_agency' } }"
          >
            <h6 class="card-header with-elements d-flex justify-content-between">
              <span class="card-header-title">
                <i class="fa fa-handshake text-cr-red mr-2"></i>
                {{ $tc('word.agency', 2) }}
              </span>
            </h6>
          </router-link>
          <v-organisation-carousel
            :organisations="organisations"
            :per-page="1"
            :autoplay-timeout="2500"
            :speed="500"
          />
        </div>
        <div class="card">
          <h6 class="card-header with-elements d-flex justify-content-between">
            <span class="card-header-title">
              <i class="fa fa-play text-cr-red mr-2"></i>
              {{ $t('sentence.review_more') }}
            </span>
          </h6>
          <div
            v-for="research in relatedResearches"
            :key="research.uuid"
            class="p-3 mb-3"
          >
            <v-reviewer-research-card
              :research="research"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchRelatedResearches,
  fetchResearch,
  fetchResearchItems,
  fetchResearchReviewSession,
} from '@/reviewer/api';
import Button from '@/common/components/Button.vue';
import ItemAnswer from '@/common/components/research/ItemAnswer.vue';
import {
  stringToPrettyDateTime,
  toPrettyDate,
  toPrettyDateTime,
} from '@/common/utils/formatting';
import ReviewerResearchCard from '@/reviewer/components/ReviewerResearchCard.vue';
import OrganisationRouterLink from '@/reviewer/components/OrganisationRouterLink.vue';
import OrganisationCarousel from '@/common/components/carousels/OrganisationCarousel.vue';
import { fetchOrganisations } from '@/public/api';

export default {
  name: 'ResearchAnswerList',
  components: {
    'v-organisation-carousel': OrganisationCarousel,
    'v-button': Button,
    'v-item-answer': ItemAnswer,
    'v-reviewer-research-card': ReviewerResearchCard,
    'v-organisation-router-link': OrganisationRouterLink,
  },
  data() {
    return {
      research: null,
      researchItems: null,
      organisations: null,
      researchReviewSession: null,
      relatedResearches: {},
    };
  },
  methods: {
    stringToPrettyDateTime,
    toPrettyDateTime,
    toPrettyDate,
    async fetch() {
      await this.fetchResearch();
      this.fetchResearchReviewSession();
      this.fetchResearchItems();
      this.fetchRelatedResearches();
      this.fetchOrganisations();
    },
    async fetchResearchItems() {
      if (!this.research) {
        alert('Research is not loaded');
        return;
      }

      const { data } = await fetchResearchItems(
        this.$route.params.uuid,
        this.research.session.uuid,
      );
      this.researchItems = data;
    },
    async fetchOrganisations() {
      const response = await fetchOrganisations('media_agency', true, true);
      if (response.status === 200) {
        this.organisations = response.data.results;
      }
    },
    async fetchRelatedResearches() {
      if (!this.research) {
        alert('Research is not loaded');
        return;
      }

      const { data } = await fetchRelatedResearches(
        this.$route.params.uuid,
      );
      this.relatedResearches = data;
    },
    async fetchResearchReviewSession() {
      if (!this.research) {
        alert('Research is not loaded');
        return;
      }

      const { data } = await fetchResearchReviewSession(this.research.session.uuid);
      this.researchReviewSession = data;
    },
    async fetchResearch() {
      const { data } = await fetchResearch(this.$route.params.uuid);
      this.research = data;
      if (!this.research.session.reviewed) {
        this.$router.push({
          name: 'review-research',
          params: { uuid: this.$route.params.uuid },
        });
      }
    },
  },
  created() {
    this.fetch();
  },
};
</script>
